import { getClosingRetrieveLoader } from "gen/endpoints/closing/closing";
import { getCommitmentInformationTableDataRetrieveLoader } from "gen/endpoints/commitment-information-table-data/commitment-information-table-data";
import i18n from "i18n/config";
import { Outlet, RouteObject } from "react-router-dom";
import { composeLoaders } from "shared/utils/RoutingUtils";

export const CLOSING_CAPITAL_EQUALIZATION = "closingCapitalEqualization";
export const CLOSING_FEE_TRUE_UP = "closingFeeTrueUp";
export const CLOSING_NON_CAPITAL_EQUALIZATION = "closingNonCapitalEqualization";

const getTabChildren = (suffix: string) => [
  {
    id: "closingCommitmentsTab" + suffix,
    path: "",
    lazy: async () => {
      const { ClosingCommitmentsTab } = await import(
        "features/closings/ClosingCommitments"
      );
      return { Component: ClosingCommitmentsTab };
    },
    loader: composeLoaders(
      getClosingRetrieveLoader(),
      getCommitmentInformationTableDataRetrieveLoader(),
      getCommitmentInformationTableDataRetrieveLoader(),
    ),
    handle: {
      title: i18n.t("common:commitments"),
    },
  },
  {
    id: CLOSING_CAPITAL_EQUALIZATION + "Tab" + suffix,
    path: "closingCapitalEqualizationTab",
    lazy: async () => {
      const { ClosingEqualizationsTab } = await import(
        "features/closings/ClosingEqualizations"
      );
      return { Component: ClosingEqualizationsTab };
    },
    handle: {
      title: i18n.t("closing:equalizationPreview"),
    },
  },
  {
    id: CLOSING_NON_CAPITAL_EQUALIZATION + "Tab" + suffix,
    path: "ClosingNonCapitalEqualizationsTab",
    lazy: async () => {
      const { ClosingNonCapitalEqualizationsTab } = await import(
        "features/closings/ClosingNonCapitalEqualizations"
      );
      return { Component: ClosingNonCapitalEqualizationsTab };
    },
    handle: {
      title: i18n.t("closing:nonCapitalEqualization"),
    },
  },
  {
    id: CLOSING_FEE_TRUE_UP + "Tab" + suffix,
    path: "feeTrueUpTab",
    lazy: async () => {
      const { ClosingFeeTrueUpsTab } = await import(
        "features/closings/ClosingFeeTrueUps"
      );
      return { Component: ClosingFeeTrueUpsTab };
    },
    handle: {
      title: i18n.t("closing:feeCatchUp"),
    },
  },
];

export const closingRoutes: RouteObject = {
  id: "closing",
  path: "closing",
  element: <Outlet />,
  children: [
    {
      id: "closingEdit",
      path: ":closingId/edit/*",
      lazy: async () => {
        const { ClosingEditPage } = await import(
          "features/closings/ClosingEditPage"
        );
        return { Component: ClosingEditPage };
      },
      loader: composeLoaders(getClosingRetrieveLoader()),
      children: [
        {
          id: "closingCommitments",
          path: "",
          lazy: async () => {
            const { ClosingCommitmentsPage } = await import(
              "features/closings/ClosingCommitments"
            );
            return { Component: ClosingCommitmentsPage };
          },
          loader: composeLoaders(
            getClosingRetrieveLoader(),
            getCommitmentInformationTableDataRetrieveLoader(),
            getCommitmentInformationTableDataRetrieveLoader(),
          ),
          handle: {
            title: i18n.t("common:commitments"),
          },
        },
        {
          id: CLOSING_CAPITAL_EQUALIZATION,
          path: "capitalEqualization",
          lazy: async () => {
            const { ClosingEqualizationsPage } = await import(
              "features/closings/ClosingEqualizations"
            );
            return { Component: ClosingEqualizationsPage };
          },
          handle: {
            title: i18n.t("closing:equalizationPreview"),
          },
        },
        {
          id: CLOSING_NON_CAPITAL_EQUALIZATION,
          path: "nonCapitalEqualization",
          lazy: async () => {
            const { ClosingNonCapitalEqualizationsPage } = await import(
              "features/closings/ClosingNonCapitalEqualizations"
            );
            return { Component: ClosingNonCapitalEqualizationsPage };
          },
          handle: {
            title: i18n.t("closing:nonCapitalEqualization"),
          },
        },
        {
          id: CLOSING_FEE_TRUE_UP,
          path: "feeTrueUp",
          lazy: async () => {
            const { ClosingFeeTrueUpsPage } = await import(
              "features/closings/ClosingFeeTrueUps"
            );
            return { Component: ClosingFeeTrueUpsPage };
          },
          handle: {
            title: i18n.t("closing:feeCatchUp"),
          },
        },
        {
          id: "closingReview",
          path: "review",
          lazy: async () => {
            const { ClosingViewPage } = await import(
              "features/closings/ClosingViewPage"
            );
            return { element: <ClosingViewPage isView={false} /> };
          },
          handle: {
            title: "Review Closing",
          },
          children: getTabChildren("Review"),
        },
      ],
    },
    {
      id: "closingView",
      path: ":closingId/view",
      lazy: async () => {
        const { ClosingViewPage } = await import(
          "features/closings/ClosingViewPage"
        );
        return { element: <ClosingViewPage isView={true} /> };
      },
      handle: {
        title: "View Closing",
      },
      loader: composeLoaders(getClosingRetrieveLoader()),
      children: getTabChildren("View"),
    },
  ],
};
